/* =============================================================
   grenlander.js — Grenlander brand page interactions
   • Scroll progress bar
   • IntersectionObserver reveal (.gl-reveal)
   • Hero stagger entrance
   • Counter animations (EaseOutExpo)
   • Category card tilt on mouse-move (desktop only)
   • Pretext-pattern Canvas text clamp for descriptions
   ============================================================= */
(function () {
  'use strict';

  /* ── Pretext-pattern Canvas measurement ── */
  var _ptxCache = new Map();

  function ptxLines(text, fontStr, containerW, lineH) {
    var key = fontStr + '|' + Math.round(containerW) + '|' + text.slice(0, 80);
    if (_ptxCache.has(key)) return _ptxCache.get(key);
    var canvas = document.createElement('canvas');
    var ctx = canvas.getContext('2d');
    ctx.font = fontStr;
    var words = text.split(/\s+/);
    var lines = 0, cur = '';
    for (var i = 0; i < words.length; i++) {
      var cand = cur ? cur + ' ' + words[i] : words[i];
      if (ctx.measureText(cand).width > containerW && cur) { lines++; cur = words[i]; }
      else { cur = cand; }
    }
    if (cur) lines++;
    var res = { lineCount: Math.max(1, lines), height: Math.max(1, lines) * lineH };
    _ptxCache.set(key, res);
    return res;
  }

  function measureH(text, fontStr, containerW, lineH) {
    if (window.__bmwPretext && typeof window.__bmwPretext.measureBlockHeightCached === 'function') {
      return window.__bmwPretext.measureBlockHeightCached(text, fontStr, containerW, lineH);
    }
    return ptxLines(text, fontStr, containerW, lineH).height;
  }

  /* ── Scroll progress bar ── */
  function initProgress() {
    var bar = document.querySelector('.gl__progress');
    if (!bar) return;
    function upd() {
      var doc = document.documentElement;
      var top = doc.scrollTop || document.body.scrollTop;
      var max = (doc.scrollHeight || document.body.scrollHeight) - doc.clientHeight;
      bar.style.width = (max > 0 ? Math.min(100, (top / max) * 100) : 0).toFixed(2) + '%';
    }
    window.addEventListener('scroll', upd, { passive: true });
    upd();
  }

  /* ── Scroll reveal ── */
  function initReveal() {
    var els = document.querySelectorAll('.gl-reveal');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target); }
      });
    }, { threshold: 0.1 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ── Hero stagger ── */
  function initHeroStagger() {
    var els = document.querySelectorAll('.gl__hero [data-stagger]');
    if (!els.length) return;
    els.forEach(function (el, i) {
      el.style.opacity = '0';
      el.style.transform = 'translateY(16px)';
      el.style.transition = 'opacity 0.55s cubic-bezier(0.16,1,0.3,1), transform 0.55s cubic-bezier(0.16,1,0.3,1)';
      el.style.transitionDelay = (i * 0.11 + 0.1) + 's';
      setTimeout(function () { el.style.opacity = '1'; el.style.transform = 'none'; }, 60);
    });
  }

  /* ── Counter animation ── */
  function animateCounter(el, target, suffix, duration) {
    suffix = suffix || ''; duration = duration || 1000;
    var start = performance.now();
    function step(now) {
      var p = Math.min((now - start) / duration, 1);
      var eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
      var val = Math.round(eased * target);
      el.textContent = (target >= 1000 ? val.toLocaleString() : val) + suffix;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function initCounters() {
    var els = document.querySelectorAll('[data-counter]');
    if (!els.length || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        animateCounter(
          el,
          parseInt(el.getAttribute('data-counter'), 10),
          el.getAttribute('data-counter-suffix') || ''
        );
        io.unobserve(el);
      });
    }, { threshold: 0.3 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ── Category card subtle tilt (desktop, no pointer:coarse) ── */
  function initCardTilt() {
    if (window.matchMedia('(pointer: coarse)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var cards = document.querySelectorAll('.gl__cat-card');
    cards.forEach(function (card) {
      card.addEventListener('mousemove', function (e) {
        var r = card.getBoundingClientRect();
        var cx = r.left + r.width / 2;
        var cy = r.top + r.height / 2;
        var rx = ((e.clientY - cy) / (r.height / 2)) * -4;
        var ry = ((e.clientX - cx) / (r.width / 2)) * 4;
        card.style.transform = 'perspective(600px) rotateX(' + rx.toFixed(2) + 'deg) rotateY(' + ry.toFixed(2) + 'deg)';
        card.style.transition = 'transform 0.08s linear';
      });
      card.addEventListener('mouseleave', function () {
        card.style.transform = '';
        card.style.transition = 'transform 0.4s cubic-bezier(0.16,1,0.3,1)';
      });
    });
  }

  /* ── Clamp category card descriptions to measured height ── */
  function initDescClamp() {
    var descs = document.querySelectorAll('.gl__cat-desc[data-clamp]');
    if (!descs.length) return;
    var maxLines = 4;
    var fontStr = '300 13px Inter, system-ui, sans-serif';
    var lineH = 23; /* 13px × 1.7 ≈ 22 → 23 */
    descs.forEach(function (el) {
      var fullText = el.textContent.trim();
      var w = el.getBoundingClientRect().width || el.offsetWidth || 220;
      var result = ptxLines(fullText, fontStr, w, lineH);
      if (result.lineCount > maxLines) {
        /* Set max-height to maxLines so overflow is hidden cleanly */
        el.style.maxHeight = (maxLines * lineH) + 'px';
        el.style.overflow = 'hidden';
      }
    });
  }

  /* ── Main ── */
  function init() {
    initProgress();
    initReveal();
    initHeroStagger();
    initCounters();
    initCardTilt();
    initDescClamp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }

  document.addEventListener('shopify:section:load', init);

})();
